import { FilterFailureTypesByFailureCode, IChartBar, IFailureCodesByFailureTypes } from "./interfaces";
import { IEventWithHours } from "components/HMIUptimeDowntimeBar/HMIUptimeDowntimeBarInterfaces";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";
import { roundToMinutes } from "../downtimeSlideUtils";

export interface FailureCodesChartResult {
    sortedData: IFailureCodesByFailureTypes[];
    isEmpty: boolean;
}

export const buildFailureCodesChartData = (
    downtimeEvents: IEventWithHours[] | undefined,
    displayUnit: string,
    language: string,
    countryCode: string | undefined,
): FailureCodesChartResult => {
    if (!downtimeEvents || downtimeEvents.length === 0) {
        return { sortedData: [], isEmpty: true };
    }

    // Group events by failure type, mapping each to a failure code record
    const failureCodesByType = downtimeEvents
        .filter((event) => event.failure_code && event.failure_code !== undefined)
        .map((event) => ({
            failure_type_id: event.failure_type.failure_type_id,
            failure_type_name: translateDefaultEntity(
                language,
                event.failure_type,
                "name",
                DefaultTranslationFieldsInTable.FAILURE_TYPE,
            ),
            machine_center_id: event.machine_center_id,
            color_code: event.failure_type.color_code,
            failure_code_id: event.failure_code.failure_code_id,
            failure_code_name: translateDefaultEntity(
                language,
                event.failure_code,
                "name",
                DefaultTranslationFieldsInTable.FAILURE_CODE,
            ),
            total_minutes: event.validTime,
            total_occurrences: 1,
        }))
        .reduce((acc: { [key: string]: FilterFailureTypesByFailureCode[] }, obj) => {
            (acc[obj.failure_type_name] = acc[obj.failure_type_name] || []).push(obj);
            return acc;
        }, {});

    // Aggregate by failure code within each type, apply minutes conversion
    const aggregated: { [key: string]: FilterFailureTypesByFailureCode[] } = {};
    Object.keys(failureCodesByType).forEach((key) => {
        const failureCodes = failureCodesByType[key];
        const grouped: { [key: number]: FilterFailureTypesByFailureCode } = {};

        failureCodes.forEach((fc) => {
            if (grouped[fc.failure_code_id]) {
                grouped[fc.failure_code_id].total_minutes += fc.total_minutes;
                grouped[fc.failure_code_id].total_occurrences += fc.total_occurrences;
            } else {
                grouped[fc.failure_code_id] = { ...fc };
            }
        });

        if (displayUnit === "minutes") {
            Object.values(grouped).forEach((item) => {
                item.total_minutes = roundToMinutes(item.total_minutes);
            });
        }

        aggregated[key] = Object.values(grouped);
    });

    // Sort by value, take top 5 per type, and re-group
    const topFiveByType = Object.values(aggregated).map((failureCodes) =>
        Object.values(failureCodes)
            .sort((a, b) =>
                displayUnit === "minutes"
                    ? b.total_minutes - a.total_minutes
                    : b.total_occurrences - a.total_occurrences,
            )
            .slice(0, 5)
            .reverse()
            .reduce((acc: { [key: string]: FilterFailureTypesByFailureCode[] }, obj) => {
                (acc[obj.failure_type_name] = acc[obj.failure_type_name] || []).push(obj);
                return acc;
            }, {}),
    );

    // Transform to chart format
    const graphData: IFailureCodesByFailureTypes[] = [];
    topFiveByType.forEach((failureCode) => {
        for (const [key, value] of Object.entries(failureCode)) {
            const failureCodesByFailureType: IChartBar[] = [];
            Object.values(value).forEach((valueObject) => {
                const chartBar: IChartBar = {};
                chartBar["countryCode"] = countryCode!;
                if ((displayUnit === "minutes" && valueObject.total_minutes) || displayUnit === "occurrences") {
                    chartBar["failureCodeName"] = valueObject.failure_code_name;
                    chartBar[`${valueObject.failure_code_name}Color`] = valueObject.color_code;
                    chartBar[`${valueObject.failure_code_name}`] =
                        displayUnit === "minutes" ? valueObject.total_minutes : valueObject.total_occurrences;
                    failureCodesByFailureType.push(chartBar);
                }
            });
            graphData.push({
                failureType: key,
                failureCodes: failureCodesByFailureType,
            });
        }
    });

    const isEmpty = graphData.every((item) => item.failureCodes.length === 0);

    // Sort failure types by total numeric value
    const sortedData = [...graphData].sort((a, b) => {
        const sumValues = (codes: IChartBar[]) =>
            codes.reduce((acc, current) => {
                const numericValues = Object.values(current).filter((val) => typeof val === "number") as number[];
                return acc + numericValues.reduce((sum, val) => sum + val, 0);
            }, 0);
        return sumValues(b.failureCodes) - sumValues(a.failureCodes);
    });

    return { sortedData, isEmpty };
};
